import { BpmCaterpillar } from './BpmCaterpillar';
import { FIGURE_LABELS, type NoteFigure } from '../types';

interface Props {
  bpm: number;
  onBpmChange: (bpm: number) => void;
  figure: NoteFigure;
  onFigureChange: (f: NoteFigure) => void;
  playing: boolean;
  onPlay: () => void;
  onStop: () => void;
  soundEnabled: boolean;
  onSoundToggle: () => void;
  isFullscreen?: boolean;
  onFullscreenToggle?: () => void;
  hideFullscreen?: boolean;
  variant?: 'default' | 'melody';
  drumEnabled?: boolean;
  onDrumAccompaniment?: () => void;
  onOpenMixVolumes?: () => void;
  dock?: 'top' | 'bottom';
}

const FIGURES: NoteFigure[] = [
  'redonda',
  'blanca',
  'negra',
  'corchea',
  'semicorchea',
];

export function TransportBar({
  bpm,
  onBpmChange,
  figure,
  onFigureChange,
  playing,
  onPlay,
  onStop,
  soundEnabled,
  onSoundToggle,
  isFullscreen,
  onFullscreenToggle,
  hideFullscreen = false,
  variant = 'default',
  drumEnabled = false,
  onDrumAccompaniment,
  onOpenMixVolumes,
  dock = 'top',
}: Props) {
  const isMelody = variant === 'melody';
  const hideFs = hideFullscreen || isMelody;

  return (
    <div
      className={[
        'transport-bar',
        isMelody ? 'transport-bar--melody' : '',
        dock === 'bottom' ? 'transport-bar--bottom' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="transport-bar__main">
        <div className="transport-group transport-bpm">
          {isMelody && <BpmCaterpillar />}
          <label className="transport-label" htmlFor="bpm-range">
            BPM: <strong>{bpm}</strong>
          </label>
          <input
            id="bpm-range"
            type="range"
            min={40}
            max={160}
            step={5}
            value={bpm}
            onChange={(e) => onBpmChange(Number(e.target.value))}
          />
        </div>

        {!isMelody && (
          <div className="transport-group transport-figures">
            <span className="transport-label">Figura para el tiempo:</span>
            <div className="figure-buttons" role="group" aria-label="Figura para subdividir un tiempo">
              {FIGURES.map((f) => (
                <button
                  key={f}
                  type="button"
                  className={`btn btn-figure ${figure === f ? 'btn-figure--active' : ''}`}
                  onClick={() => onFigureChange(f)}
                  aria-pressed={figure === f}
                >
                  {FIGURE_LABELS[f]}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="transport-actions">
        {isMelody && onDrumAccompaniment && (
          <button
            type="button"
            className={`btn btn-secondary btn-drum ${drumEnabled ? 'btn-drum--on' : ''}`}
            onClick={onDrumAccompaniment}
            aria-pressed={drumEnabled}
            title="Crear acompañamiento rítmico"
          >
            <span className="btn-icon-glyph" aria-hidden="true">🥁</span>
            Acompañamiento
          </button>
        )}
        {isMelody && onOpenMixVolumes && (
          <button
            type="button"
            className="btn btn-secondary btn-mix"
            onClick={onOpenMixVolumes}
            title="Nivelar volúmenes"
          >
            <span className="btn-icon-glyph" aria-hidden="true">🎚</span>
            Volúmenes
          </button>
        )}
        <button
          type="button"
          className={`btn btn-secondary btn-sound ${soundEnabled ? 'btn-sound--on' : ''}`}
          onClick={onSoundToggle}
          aria-pressed={soundEnabled}
          title={soundEnabled ? 'Sonido activado' : 'Sonido desactivado'}
        >
          <span className="btn-icon-glyph" aria-hidden="true">
            {soundEnabled ? '🔊' : '🔇'}
          </span>
          Sonido
        </button>
        {!hideFs && onFullscreenToggle && (
          <button
            type="button"
            className="btn btn-secondary btn-fullscreen"
            onClick={onFullscreenToggle}
            aria-pressed={isFullscreen}
          >
            <span className="btn-icon-glyph" aria-hidden="true">
              {isFullscreen ? '⤢' : '⛶'}
            </span>
            {isFullscreen ? 'Salir pantalla completa' : 'Pantalla completa'}
          </button>
        )}
        {!playing ? (
          <button type="button" className="btn btn-play" onClick={onPlay}>
            <span className="btn-play__icon" aria-hidden="true">▶</span>
            Reproducir
          </button>
        ) : (
          <button type="button" className="btn btn-stop" onClick={onStop}>
            ■ Detener
          </button>
        )}
      </div>
    </div>
  );
}
